import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, LogIn, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Tier } from "@/lib/tier";

const TIER_OPTIONS: Tier[] = ["starter", "plus", "pro", "elite", "agency", "comp", "admin_workspace", "super_admin"];

export function MyWorkspacesTab() {
  const { user } = useAuth();
  const { allOwnedWorkspaces: workspaces, refresh, setActive, active } = useWorkspace();
  const navigate = useNavigate();
  const [newPersonaLabel, setNewPersonaLabel] = useState("");
  const [newPersonaTier, setNewPersonaTier] = useState<Tier>("starter");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");

  const logAction = async (action: string, metadata: Record<string, unknown>) => {
    if (!user) return;
    const { error } = await supabase
      .from("admin_actions")
      .insert([{ admin_user_id: user.id, action, metadata: metadata as Json }]);
    if (error) toast.error(`Audit log failed: ${error.message}`);
  };

  const startEdit = (id: string, current: string) => { setEditingId(id); setEditLabel(current); };

  const saveEdit = async (id: string) => {
    const label = editLabel.trim();
    if (!label) return toast.error("Label cannot be empty");
    const { error } = await supabase.from("profiles").update({ workspace_label: label, display_name: label }).eq("id", id);
    if (error) return toast.error(error.message);
    await logAction("rename_workspace", { workspace_id: id, label });
    setEditingId(null);
    await refresh();
    toast.success("Renamed");
  };

  const changeTier = async (id: string, label: string, tier: Tier) => {
    const { error } = await supabase.from("profiles").update({ account_tier: tier }).eq("id", id);
    if (error) return toast.error(error.message);
    await logAction("change_tier", { workspace_id: id, label, tier });
    await refresh();
    toast.success(`Tier set to ${tier}`);
  };

  const addPersona = async () => {
    if (!user || !newPersonaLabel.trim()) return;
    const nextOrder = workspaces.reduce((m, w) => Math.max(m, w.sort_order), -1) + 1;
    const { data, error } = await supabase
      .from("profiles")
      .insert({
        owner_user_id: user.id,
        display_name: newPersonaLabel.trim(),
        workspace_label: newPersonaLabel.trim(),
        is_primary: true,
        account_tier: newPersonaTier,
        sort_order: nextOrder,
      })
      .select("id").single();
    if (error) return toast.error(error.message);
    await logAction("create_persona", { workspace_id: data.id, label: newPersonaLabel.trim(), tier: newPersonaTier });
    setNewPersonaLabel("");
    await refresh();
    setActive(data.id);
    toast.success("Persona created");
  };

  const personas = workspaces.filter((w) => w.is_primary);

  const renderRow = (workspace: typeof workspaces[number]) => {
    const label = workspace.workspace_label || workspace.display_name || "Untitled";
    const isEditing = editingId === workspace.id;
    return (
      <div key={workspace.id} className="flex min-w-0 flex-col gap-3 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {isEditing ? (
            <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(workspace.id)} autoFocus className="max-w-xs" />
          ) : (
            <span className="truncate font-medium">{label}</span>
          )}
          <Select value={workspace.account_tier} onValueChange={(v) => changeTier(workspace.id, label, v as Tier)}>
            <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIER_OPTIONS.map((tier) => <SelectItem key={tier} value={tier} className="text-xs">{tier}</SelectItem>)}
            </SelectContent>
          </Select>
          {workspace.is_primary && <Badge className="gap-1"><Crown className="h-3 w-3" />Persona</Badge>}
          {active?.id === workspace.id && <Badge variant="outline">Active</Badge>}
        </div>
        <div className="flex flex-wrap items-center gap-1 sm:flex-nowrap sm:justify-end">
          {isEditing ? (
            <>
              <Button variant="ghost" size="icon" onClick={() => saveEdit(workspace.id)} aria-label="Save"><Check className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => setEditingId(null)} aria-label="Cancel"><X className="h-4 w-4" /></Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="icon" onClick={() => startEdit(workspace.id, label)} aria-label="Rename"><Pencil className="h-4 w-4" /></Button>
              {active?.id !== workspace.id && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1"
                  onClick={() => {
                    setActive(workspace.id);
                    toast.success(`Entered ${label}`);
                    navigate("/");
                  }}
                >
                  <LogIn className="h-3.5 w-3.5" /> Enter
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-soft">
        <CardHeader><CardTitle className="flex items-center gap-2"><Crown className="h-4 w-4 text-primary" /> Your Personas</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {personas.length === 0 && <p className="text-sm text-muted-foreground">No Personas yet.</p>}
          {personas.map(renderRow)}
          <div className="flex flex-col gap-2 pt-2 sm:flex-row">
            <Input
              placeholder="New Persona label (e.g. 'Acme Test Account')"
              value={newPersonaLabel}
              onChange={(e) => setNewPersonaLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addPersona()}
            />
            <Select value={newPersonaTier} onValueChange={(v) => setNewPersonaTier(v as Tier)}>
              <SelectTrigger className="sm:w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIER_OPTIONS.map((tier) => <SelectItem key={tier} value={tier}>{tier}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={addPersona} className="sm:shrink-0">Add Persona</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Each Persona is its own top-level account with its own tier and isolated library. Super-admins are exempt from per-tier usage limits when acting on Personas they own.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
